import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { getActiveMissionV2 } from 'graphql/queries';
import { onUpdateActiveMissionByVin } from 'graphql/subscriptions';
import { SafeMissionV2Schema } from 'helper/v2Schemas';

import { AppError } from './errors';
import { fetchV2 } from './utils';

import type { GetActiveMissionV2Query, MissionV2 } from 'API';

const extractMission = (
    result: { __typename?: string; errorCode?: string; statusCode?: number } | null | undefined
): MissionV2 => {
    const isError = !result || result.__typename === 'ErrorResponse' || result.errorCode !== undefined;
    if (isError) {
        throw new AppError(result?.errorCode ?? 'Mission not found', {
            errorCode: result?.errorCode,
            statusCode: result?.statusCode,
        });
    }
    return result as unknown as MissionV2;
};

const parseMission = (data: MissionV2) => SafeMissionV2Schema().parse(data);

const getMissionV3QueryFn = (vin: string) =>
    fetchV2(
        () => client.graphql({ query: getActiveMissionV2, variables: { vin } }),
        (data: GetActiveMissionV2Query) =>
            data?.getActiveMissionV2 != null ? parseMission(extractMission(data.getActiveMissionV2)) : null,
        '[getMissionV3QueryFn]: Unexpected error occurred while fetching V3 mission'
    );

export const missionV3Api = createApi({
    reducerPath: 'missionV3',
    baseQuery: graphqlRequestBaseQuery({ url: '/graphql' }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        getMissionV3: builder.query<MissionV2 | null, string>({
            queryFn: getMissionV3QueryFn,
        }),
        onMissionV3Subscription: builder.query<MissionV2 | null, string>({
            queryFn: getMissionV3QueryFn,
            onCacheEntryAdded: async (vin, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) => {
                await cacheDataLoaded;
                if (import.meta.env.DEV) {
                    await cacheEntryRemoved;
                } else {
                    const connection = client
                        .graphql({ query: onUpdateActiveMissionByVin, variables: { vin } })
                        .subscribe({
                            next: ({ data }) => {
                                if (!data.onUpdateActiveMissionByVin) {
                                    console.error(
                                        '[onMissionV3Subscription]: Please make sure all the attributes are returned in the mutation'
                                    );
                                    return;
                                }
                                const payload = data.onUpdateActiveMissionByVin.payload;
                                const hasMission = 'id' in payload && payload.id != null;
                                updateCachedData(() =>
                                    hasMission
                                        ? parseMission(extractMission(data.onUpdateActiveMissionByVin.payload))
                                        : null
                                );
                            },
                            error: error => console.warn(error),
                        });
                    cacheEntryRemoved
                        .then(() => {
                            connection.unsubscribe();
                        })
                        .catch(error => console.error(error));
                }
            },
        }),
    }),
});

export const { useOnMissionV3SubscriptionQuery } = missionV3Api;
